import BigNumber from 'bignumber.js'
import Web3 from 'web3'
import { ethers } from 'ethers'
import { useState, useEffect, useCallback } from 'react'
import { provider } from 'web3-core'
import { useWallet } from '@binance-chain/bsc-use-wallet'
import useRefresh from 'hooks/useRefresh'
import { 
    useSep20Factory, useERC20, useSep20TemplateBasic, useSep20FactoryTokenBasic, 
    useSep20FactoryTokenLiquidityGenerator, useSep20FactoryTokenAnnuity, useSep20FactoryTokenMultiMint, useSep20FactoryTokenInscriptions
} from 'hooks/useContract'
import { getBasicTokenInfo, transfer, getTokenBalance } from 'utils/erc20'
import { getGenesisAddress,getNullAddress,getEbenAddress } from 'utils/addressHelpers'
import { 
    getFeeToken, getCreationFee, createSep20TokenBasic, createSep20TokenLiquidityProvider, createSep20TokenAnnuity,
    getSep20Template, getTemplateType, createSep20TokenMultiMint, checkMultiMintAddressStatus, multiMintChangeAllowanceToMint, createSep20TokenInscriptions,
    getMySep20Tokens, getRecentSep20Tokens, getSingleSep20Token, mint, transferOwnership, 
    checkLiquidityGeneratorWhiteList, addToLiquidityGeneratorWhitelist, removeFromLiquidityGeneratorWhitelist,
    annuityCollectPayout, annuityForcePayout, annuitySwitchToPayout, annuitySwitchToStaking, stakeToAnnuity, mintInscriptions
} from 'utils/sep20FactoryUtils'

declare const window: any;

export const useFeeToken = () => {
    const [ feeToken, setFeeToken ] = useState({});
    const contract = useSep20Factory()
    const { slowRefresh } = useRefresh();
  
    useEffect(() => {
        const fetch = async () => {
            const feeTokenAddress = await getFeeToken(contract);
            const tokenInfo:any = {};
            await Promise.all([
                getBasicTokenInfo(feeTokenAddress).then((info)=>{
                    Object.assign(tokenInfo, info);
                }),
                getCreationFee(contract).then((fee)=>{
                    Object.assign(tokenInfo, {creationFee: new BigNumber(fee) });
                })
            ]);
            tokenInfo.creationFee = tokenInfo.creationFee.div(new BigNumber(10).pow(tokenInfo.decimals)).toNumber();
            setFeeToken(tokenInfo);
        }
  
        if (contract) fetch()
    }, [slowRefresh, contract])
  
    return feeToken;
}

export const useEbenBalance = (extraDependency=null) => {
    const [balance, setBalance] = useState(new BigNumber(0))
    const { account, ethereum }: { account: string; ethereum: provider } = useWallet()
    const { slowRefresh } = useRefresh()
  
    useEffect(() => {
      const fetchBalance = async () => {
        const res = await getTokenBalance(ethereum, getEbenAddress(), account)
        setBalance(new BigNumber(res).div(new BigNumber(10).pow(18)))
      }
  
      if (account && ethereum) {
        fetchBalance()
      }
    }, [account, ethereum, extraDependency, slowRefresh])
  
    return balance
}

export const useCreateSep20TokenBasic = () => {
    const { account } = useWallet()
    const contract = useSep20Factory()

    const handleCreate = useCallback(
        async (name: any, symbol: any, decimals: any, initialSupply: BigNumber, isMintable: boolean) => {
            try {
                const adjustedInitialSupply = initialSupply.times(new BigNumber(10).pow(decimals)).toFixed(0);
                const txHash = await createSep20TokenBasic(contract, account, name, symbol, decimals, adjustedInitialSupply, isMintable);
                return txHash
            } catch (e) {
                return false
            }
        },
        [contract, account],
    )

    return handleCreate;
}

export const useCreateSep20TokenLiquidityProvider = () => {
    const { account } = useWallet()
    const contract = useSep20Factory()

    const handleCreate = useCallback(
        async (name: any, symbol: any, initialSupply: BigNumber, charityAddress: string, taxFeeBp: any, liquidityFeeBp: any, charityFeeBp: any) => {
            try {
                const adjustedInitialSupply = initialSupply.times(new BigNumber(10).pow(9)).toFixed(0);
                let ca = charityAddress;
                let cbp: any = charityFeeBp;
                if (!charityAddress || !Web3.utils.isAddress(charityAddress)) {
                    ca = getGenesisAddress();
                    cbp = "0";
                }
                const txHash = await createSep20TokenLiquidityProvider(contract, account, name, symbol, adjustedInitialSupply, ca, taxFeeBp, liquidityFeeBp, cbp);
                return txHash
            } catch (e) {
                return false
            }
        },
        [contract, account],
    )

    return handleCreate;
}

export const useCreateSep20TokenAnnuity = () => {
    const { account } = useWallet()
    const contract = useSep20Factory()

    const handleCreate = useCallback(
        async (name: any, symbol: any, initialSupply: BigNumber, payoutPeriod: any, endInterestTime: any, apyBP: any) => {
            try {
                const adjustedInitialSupply = initialSupply.times(new BigNumber(10).pow(18)).toFixed(0);
                const txHash = await createSep20TokenAnnuity(contract, account, name, symbol, adjustedInitialSupply, payoutPeriod, endInterestTime, apyBP);
                return txHash
            } catch (e) {
                return false
            }
        },
        [contract, account],
    )

    return handleCreate;
}

export const useCreateSep20TokenMultiMint = () => {
    const { account } = useWallet()
    const contract = useSep20Factory()

    const handleCreate = useCallback(
        async (name: any, symbol: any, decimals: any, initialSupply: BigNumber) => {
            try {
                const adjustedInitialSupply = initialSupply.times(new BigNumber(10).pow(decimals)).toFixed(0);
                const txHash = await createSep20TokenMultiMint(contract, account, name, symbol, decimals, adjustedInitialSupply);
                return txHash
            } catch (e) {
                return false
            }
        },
        [contract, account],
    )

    return handleCreate;
}

export const useCreateSep20TokenInscriptions = () => {
    const { account } = useWallet()
    const contract = useSep20Factory()

    const handleCreate = useCallback(
        async (name: any, symbol: any, decimals: any, supplyCap: BigNumber, mintAmount: BigNumber, mintCap: any, mintFee: BigNumber) => {
            try {
                const adjustedSupplyCap = supplyCap.times(new BigNumber(10).pow(decimals)).toFixed(0);
                const adjustedMintAmount = mintAmount.times(new BigNumber(10).pow(decimals)).toFixed(0);
                const adjustedMintFee = mintFee.times(new BigNumber(10).pow(18)).toFixed(0);
                const txHash = await createSep20TokenInscriptions(contract, account, name, symbol, decimals, adjustedSupplyCap, adjustedMintAmount, mintCap, adjustedMintFee);
                return txHash
            } catch (e) {
                return false
            }
        },
        [contract, account],
    )

    return handleCreate;
}

export const useMySep20Tokens = (currentPage) => {
    const [ tokens, setTokens ] = useState({
        currentPage: 1,
        pageCount: 0,
        tokens: [],
        isLoading: true
    });
    const { account } = useWallet();
    const contract = useSep20Factory()
    const { fastRefresh } = useRefresh();

    useEffect(() => {
        const fetch = async () => {
            let myTokens: any = false;
            if (account) {
                myTokens = await getMySep20Tokens(contract, account, currentPage);
            } else {
                myTokens = {
                    currentPage: 1,
                    pageCount: 0,
                    tokens: [],
                    isLoading: true
                };
            }
            if (myTokens !== false) setTokens(myTokens);
        }
  
        if (contract) fetch()
    }, [fastRefresh, contract, account, currentPage])
  
    return tokens;
}

export const useRecentSep20Tokens = (currentPage) => {
    const [ tokens, setTokens ] = useState({
        currentPage: 1,
        pageCount: 0,
        count: 0,
        tokens: [],
        isLoading: true
    });
    const contract = useSep20Factory()
    const { fastRefresh } = useRefresh();
    const { account } = useWallet();

    useEffect(() => {
        const fetch = async () => {
            const recentTokens = await getRecentSep20Tokens(contract, currentPage, account);
            if (recentTokens !== false) setTokens(recentTokens);
        }
  
        if (contract) fetch()
    }, [fastRefresh, contract, currentPage, account])
  
    return tokens;
}

export const useSingleSep20Token = (tokenAddress) => {
    const [ tokens, setTokens ] = useState([]);
    const contract = useSep20Factory()
    const { fastRefresh } = useRefresh();
    const { account } = useWallet();

    useEffect(() => {
        const fetch = async () => {
            const singleTokenArray = await getSingleSep20Token(contract, ethers.utils.getAddress(tokenAddress), account);
            if (singleTokenArray !== false) setTokens(singleTokenArray);
        }
  
        if (contract && tokenAddress) fetch()
    }, [fastRefresh, contract, tokenAddress, account])
  
    return tokens;
}

export const useMintBasicToken = (tokenAddress) => {
    const { account } = useWallet()
    const contract = useSep20FactoryTokenBasic(tokenAddress);

    const handleMint = useCallback(
        async (amount: BigNumber, decimals: any) => {
            try {
                const adjustedAmount = amount.times(new BigNumber(10).pow(decimals)).toFixed(0);
                const txHash = await mint(contract, adjustedAmount, account);
                return txHash
            } catch (e) {
                return false
            }
        },
        [contract, account],
    )

    return handleMint;
}

export const useMintInscriptionsToken = (tokenAddress) => {
    const { account } = useWallet()
    const contract = useSep20FactoryTokenInscriptions(tokenAddress);

    const handleMint = useCallback(
        async () => {
            try {
                const txHash = await mintInscriptions(contract, account);
                return txHash
            } catch (e) {
                return false
            }
        },
        [contract, account],
    )

    return handleMint;
}

export const useBurn = (token) => {
    const { account } = useWallet()
    const contract = useERC20(token.address)

    const handleBurn = useCallback(
        async (amount: BigNumber) => {
            try {
                const txHash = await transfer(contract, getNullAddress(), amount.times(new BigNumber(10).pow(token.decimals)).toString(), account);
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract, token],
    )

    return handleBurn;
}

export const useTransferOwnership = (tokenAddress) => {
    const { account } = useWallet()
    const contract = useSep20FactoryTokenBasic(tokenAddress)

    const handleTransferOwnership = useCallback(
        async (newOwner: any) => {
            try {
                const txHash =  await transferOwnership(contract, newOwner, account);
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract],
    )

    return handleTransferOwnership;
}

export const useRenounce = (tokenAddress) => {
    const { account } = useWallet()
    const contract = useSep20FactoryTokenBasic(tokenAddress)

    const handleRenounce = useCallback(
        async () => {
            try {
                const txHash =  await transferOwnership(contract, getNullAddress(), account);
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract],
    )

    return handleRenounce;
}

export const useCheckLiquidityGeneratorWhiteList = (tokenAddress: string, targetAddress: string) => {
    const [isExcluded, setIsExcluded] = useState(false)
    const contract = useSep20FactoryTokenLiquidityGenerator(tokenAddress);

    useEffect(() => {
      const fetch = async () => {
        const res = await checkLiquidityGeneratorWhiteList(contract, targetAddress)
        setIsExcluded(res)
      }
  
      if (targetAddress) fetch();
      else setIsExcluded(false);
    }, [contract, targetAddress])
  
    return isExcluded;
}

export const useAddToLiquidityGeneratorWhitelist = (tokenAddress) => {
    const { account } = useWallet()
    const contract = useSep20FactoryTokenLiquidityGenerator(tokenAddress)

    const handle = useCallback(
        async (address: any) => {
            try {
                const txHash =  await addToLiquidityGeneratorWhitelist(contract, account, address);
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract],
    )

    return handle;
}

export const useRemoveFromLiquidityGeneratorWhitelist = (tokenAddress) => {
    const { account } = useWallet()
    const contract = useSep20FactoryTokenLiquidityGenerator(tokenAddress)

    const handle = useCallback(
        async (address: any) => {
            try {
                const txHash =  await removeFromLiquidityGeneratorWhitelist(contract, account, address);
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract],
    )

    return handle;
}

export const useStakeToAnnuity = (tokenAddress) => {
    const { account } = useWallet()
    const contract = useSep20FactoryTokenAnnuity(tokenAddress)

    const handle = useCallback(
        async (amount: BigNumber) => {
            try {
                const txHash =  await stakeToAnnuity(contract, account, amount.times(new BigNumber(10).pow(18)).toFixed(0));
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract],
    )

    return handle;
}

export const useAnnuitySwitchToStaking = (tokenAddress) => {
    const { account } = useWallet()
    const contract = useSep20FactoryTokenAnnuity(tokenAddress)

    const handle = useCallback(
        async () => {
            try {
                const txHash =  await annuitySwitchToStaking(contract, account);
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract],
    )

    return handle;
}

export const useAnnuitySwitchToPayout = (tokenAddress) => {
    const { account } = useWallet()
    const contract = useSep20FactoryTokenAnnuity(tokenAddress)

    const handle = useCallback(
        async () => {
            try {
                const txHash =  await annuitySwitchToPayout(contract, account);
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract],
    )

    return handle;
}

export const useAnnuityCollectPayout = (tokenAddress) => {
    const { account } = useWallet()
    const contract = useSep20FactoryTokenAnnuity(tokenAddress)

    const handle = useCallback(
        async () => {
            try {
                const txHash =  await annuityCollectPayout(contract, account);
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract],
    )

    return handle;
}

export const useAnnuityForcePayout = (tokenAddress) => {
    const { account } = useWallet()
    const contract = useSep20FactoryTokenAnnuity(tokenAddress)

    const handle = useCallback(
        async (amount: BigNumber) => {
            try {
                const txHash =  await annuityForcePayout(contract, account, amount.times(new BigNumber(10).pow(18)).toFixed(0));
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract],
    )

    return handle;
}

export const useCheckMultiMintAddressStatus = (tokenAddress: string, targetAddress: string) => {
    const [status, setStatus] = useState<any>(false)
    const contract = useSep20FactoryTokenMultiMint(tokenAddress);

    useEffect(() => {
      const fetch = async () => {
        const res = await checkMultiMintAddressStatus(contract, targetAddress)
        setStatus(res)
      }
  
      if (targetAddress) fetch();
      else setStatus(false);
    }, [contract, targetAddress])
  
    return status;
}

export const useMultiMintSetAllowanceToMint = (tokenAddress) => {
    const { account } = useWallet()
    const contract = useSep20FactoryTokenMultiMint(tokenAddress)

    const handle = useCallback(
        async (minter:any, amount: BigNumber, decimals: any) => {
            try {
                const txHash =  await multiMintChangeAllowanceToMint(contract, account, minter, amount.times(new BigNumber(10).pow(decimals)).toFixed(0));
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract],
    )

    return handle;
}

export const useMultiMintSetMaxAllowanceToMint = (tokenAddress) => {
    const { account } = useWallet()
    const contract = useSep20FactoryTokenMultiMint(tokenAddress)

    const handle = useCallback(
        async (minter:any) => {
            try {
                const txHash =  await multiMintChangeAllowanceToMint(contract, account, minter, ethers.constants.MaxUint256);
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract],
    )

    return handle;
}

export const useMultiMintRevokeAllowanceToMint = (tokenAddress) => {
    const { account } = useWallet()
    const contract = useSep20FactoryTokenMultiMint(tokenAddress)

    const handle = useCallback(
        async (minter:any) => {
            try {
                const txHash =  await multiMintChangeAllowanceToMint(contract, account, minter, 0);
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract],
    )

    return handle;
}
